<template>
  <v-form
    ref="formRef"
    validate-on="submit"
    class="custom-form"
    @submit.prevent="onSubmit"
  >
    <v-text-field
      v-model="username"
      label="Username"
      variant="outlined"
      class="custom-input"
      :rules="[r.required]"
      autocomplete="username"
      autofocus
      hide-details="auto"
      @keyup.enter="focusPassword"
    />
    <v-text-field
      ref="passwordField"
      v-model="password"
      label="Password"
      variant="outlined"
      class="custom-input"
      :type="showPassword ? 'text' : 'password'"
      :rules="[r.required]"
      autocomplete="current-password"
      hide-details="auto"
      :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
      @keyup.enter="onSubmit"
      @click:append-inner="showPassword = !showPassword"
    />

    <v-btn
      :loading="loading"
      type="submit"
      class="custom-button"
      block
    >
      Login
    </v-btn>

    <div class="divider-container">
      <v-divider />
      <span class="divider-text">or</span>
      <v-divider />
    </div>

    <v-btn
      class="google-button"
      block
      @click="$emit('google-login')"
    >
      <svg
        class="google-icon"
        viewBox="0 0 24 24"
        width="18"
        height="18"
      >
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
      Sign in with Google
    </v-btn>

    <v-btn
      variant="text"
      class="mt-2 center-link"
      @click="$emit('forgot-password')"
    >
      Forgot password?
    </v-btn>
  </v-form>
</template>

<script setup lang="ts">
  import { ref, nextTick } from 'vue';
  import type { VTextField } from 'vuetify/components';

  const { loading } = defineProps<{ loading?: boolean }>();

  const emit = defineEmits<{
    (e: 'submit', payload: { username: string; password: string }): void;
    (e: 'forgot-password'): void;
    (e: 'google-login'): void;
  }>();

  const username = ref('');
  const password = ref('');
  const showPassword = ref(false);
  const passwordField = ref<InstanceType<typeof VTextField> | null>(null);
  const formRef = ref();

  const r = { required: (v: string) => !!v || 'Required' };

  async function focusPassword() {
    await nextTick();
    passwordField.value?.focus();
  }

  async function onSubmit() {
    if (!username.value || !password.value) return;
    emit('submit', { username: username.value, password: password.value });
  }
</script>

<style scoped lang="sass">
  .custom-form
    width: 100%
    height: 100%
    display: flex
    flex-direction: column

  .custom-input
    width: 100% !important
    margin-bottom: 24px
    font-size: 16px
    :deep(.v-field)
      border-radius: 10px
      --v-field-border-color: #ccc
      width: 100%

  .custom-button
    border: 2px solid #1976d2
    background-color: transparent
    color: #1976d2
    font-weight: 400
    font-size: 16px
    height: 50px
    border-radius: 10px
    margin-top: 32px
    text-transform: none
    transition: all 0.2s ease
    &:hover
      background-color: rgba(25, 118, 210, 0.04)
      border-color: #1565c0
      color: #1565c0

  .center-link
    align-self: center
    margin-top: 12px

  .divider-container
    display: flex
    align-items: center
    gap: 16px
    margin: 24px 0

    .v-divider
      flex: 1

    .divider-text
      color: #666
      font-size: 14px

  .google-button
    border: 1px solid #dadce0
    background-color: #fff
    color: #3c4043
    font-weight: 500
    font-size: 14px
    height: 50px
    border-radius: 10px
    text-transform: none
    transition: all 0.2s ease
    &:hover
      background-color: #f8f9fa
      border-color: #d2e3fc

    .google-icon
      margin-right: 8px

  :deep(.v-input.v-input--error)
    .v-label, .v-field__outline, .v-messages__message
      color: #f57c00 !important
      border-color: #f57c00 !important

  :deep(.v-input.v-input--error .v-field--focused .v-field__outline)
    border-color: #f57c00 !important
    box-shadow: 0 0 0 2px rgba(245,124,0,0.15) !important
</style>
